import browserI18next, { createInstance, type i18n } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import { zh } from "./zh";
import { en } from "./en";
const isServer = import.meta.env?.SSR === true || typeof window === "undefined";
const i18next = isServer ? createInstance() : browserI18next;

// Preserve an existing browser preference while the SSR locale pins the first hydration pass.
export const storedHydrationLocale = (() => {
	try { return !isServer && document.documentElement.dataset?.ssrLocale ? window.localStorage.getItem("muhaha:locale") : null; }
	catch { return null; }
})();

export type Locale = "zh" | "en";
export const DEFAULT_LOCALE: Locale = "en";
export const LOCALES: Locale[] = ["en", "zh"];
const LOCALE_STORAGE_KEY = "muhaha:locale";
let requestI18n: (() => i18n | undefined) | undefined;
export function setRequestI18n(resolver: () => i18n | undefined) { requestI18n = resolver; }
export function getI18n() { return requestI18n?.() ?? i18next; }
export function createPageI18n(locale: Locale) {
	const instance = createInstance();
	void instance.init({ resources: { zh: { translation: zh }, en: { translation: en } }, lng: locale, fallbackLng: DEFAULT_LOCALE, interpolation: { escapeValue: false }, initAsync: false });
	return instance;
}

/**
 * Older builds cached navigator values such as `zh-CN`. Those were not an
 * explicit language choice, and they also made the Settings control report
 * English while i18next resolved Chinese. Keep only locales written by the
 * language switcher; every other legacy/browser-derived value migrates to the
 * English-first default.
 */
function removeUnsupportedStoredLocale() {
	try {
		const storedLocale = window.localStorage.getItem(LOCALE_STORAGE_KEY);
		if (storedLocale && !LOCALES.includes(storedLocale as Locale)) {
			window.localStorage.removeItem(LOCALE_STORAGE_KEY);
		}
	} catch {
		// LanguageDetector will use the English fallback when storage is unavailable.
	}
}

/** i18next.language normalized to a supported locale. */
export function currentLocale(): Locale {
	const current = getI18n();
	const resolved = (current.resolvedLanguage || current.language || DEFAULT_LOCALE).toLowerCase();
	return resolved === "zh" || resolved.startsWith("zh-") ? "zh" : "en";
}

/** BCP 47 tag for Intl formatters. */
export function intlLocale(): string {
	return currentLocale() === "zh" ? "zh-CN" : "en";
}

/**
 * Normalizes a free-form language tag (e.g. a `?lang=` query param) to a
 * supported locale. Case-insensitive and matches by prefix, so "zh-CN",
 * "en-US", etc. resolve too. Unrecognized values return null.
 */
export function normalizeLocale(value: string | null | undefined): Locale | null {
	if (!value) return null;
	const lower = value.toLowerCase();
	if (lower.startsWith("zh")) return "zh";
	if (lower.startsWith("en")) return "en";
	return null;
}

function applyDocumentLocale() {
	if (isServer || typeof document === "undefined") return;
	document.documentElement.lang = intlLocale();
	if (!document.documentElement.dataset?.ssrLocale) document.title = i18next.t("app.title");
}

removeUnsupportedStoredLocale();

// Resources are bundled, so init completes synchronously — no Suspense needed.
if (!isServer) i18next.use(LanguageDetector);
void i18next
	.use(initReactI18next)
	.init({
		lng: !isServer && typeof document !== "undefined" ? document.documentElement.dataset?.ssrLocale : DEFAULT_LOCALE,
		resources: { zh: { translation: zh }, en: { translation: en } },
		fallbackLng: DEFAULT_LOCALE,
		supportedLngs: LOCALES,
		load: "languageOnly",
		interpolation: { escapeValue: false },
		detection: {
			// English-first: honor an explicit saved choice, but do not infer the
			// initial UI language from the browser/device locale.
			order: ["localStorage"],
			caches: ["localStorage"],
			lookupLocalStorage: LOCALE_STORAGE_KEY,
		},
	});

i18next.on("languageChanged", applyDocumentLocale);
i18next.on("languageChanged", (locale) => {
	if (typeof document !== "undefined") document.cookie = `muhaha-locale=${normalizeLocale(locale) ?? DEFAULT_LOCALE}; Path=/; SameSite=Lax; Max-Age=31536000`;
});
applyDocumentLocale();

export { i18next };
